// Catálogo showcase NULED — REVISTA impresa, sin precios (regla de negocio).
// Plantilla determinística (100% controlada) + Paged.js para hojas carta reales.
// Ruta: /catalogo/pdf?brand=nuled|lutron|all  ó  ?ids=<uuid,...>&titulo=<opcional>

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { thumbUrl } from '../lib/imageUrl';
import { theme } from '../styles/theme';

type Product = {
  id: string; sku: string; brand_code: string; name: string;
  short_description: string | null; description: string | null;
  specs: Record<string, unknown> | null; images: unknown; category_paths: string[] | null;
};
const BRAND = '#2D484F', ACCENT = '#CCCC33';

type ImageObj = { url?: string; kind?: string; is_thumbnail?: boolean };
function heroImage(raw: unknown): string | null {
  if (!Array.isArray(raw)) return null; const a = raw as ImageObj[];
  return (a.find(i => i.kind === 'producto' || i.kind === 'foto')?.url ?? a.find(i => i.is_thumbnail)?.url ?? a[0]?.url) ?? null;
}
function appImage(raw: unknown): string | null {
  if (!Array.isArray(raw)) return null; const a = raw as ImageObj[];
  return a.find(i => i.kind === 'aplicacion' || i.kind === 'ambiance')?.url ?? null;
}
function strip(s: string): string {
  return (s || '').replace(/<[^>]*>/g, ' ').replace(/&aacute;/gi, 'á').replace(/&eacute;/gi, 'é').replace(/&iacute;/gi, 'í').replace(/&oacute;/gi, 'ó').replace(/&uacute;/gi, 'ú').replace(/&ntilde;/gi, 'ñ').replace(/&deg;/gi, '°').replace(/&[a-z]+;/gi, ' ').replace(/\s+/g, ' ').trim();
}
function summaryFor(p: Product): string {
  const s = strip(p.short_description || '') || strip(p.description || '');
  return s.length > 150 ? s.slice(0, 147) + '…' : s;
}
function keySpecs(specs: Record<string, unknown> | null): { label: string; value: string }[] {
  if (!specs) return []; const out: { label: string; value: string }[] = [];
  const order: [RegExp, string][] = [[/potenc/, 'Potencia'], [/voltaj/, 'Voltaje'], [/(cct|temperatura)/, 'Temp. color'], [/cri|reproduc/, 'CRI'], [/(ip|protecc)/, 'IP'], [/(angul|apertur)/, 'Ángulo'], [/lumen/, 'Flujo'], [/material/, 'Material'], [/(dimension|medida)/, 'Dimensiones']];
  for (const [rx, label] of order) { for (const k of Object.keys(specs)) { if (rx.test(k.toLowerCase())) { const v = specs[k]; if (v != null && String(v).trim() !== '') { out.push({ label, value: String(v) }); break; } } } if (out.length >= 6) break; }
  return out;
}
function catL1(p: Product): string { const cp = p.category_paths; return (cp && cp[0]) ? cp[0].split('/')[0] : (p.brand_code === 'lutron' ? 'Lutron' : 'NULED'); }
function catL2(p: Product): string | null { const cp = p.category_paths; if (cp && cp[1]) { const s = cp[1].split('/').pop(); if (s && s !== catL1(p)) return s; } return null; }
function esc(s: string): string { return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

const LOGO = `<svg viewBox="0 0 245.417 58.032" style="height:46px" aria-label="NULED"><path d="M3.289,0,32.517,56.97l.126.247H36.6L7.244,0Z" transform="translate(1.483)" fill="#fff"/><rect width="3.517" height="57.216" fill="#fff"/><rect width="3.519" height="57.216" transform="translate(39.336 0)" fill="#fff"/><path d="M77.875,0V35.545c0,12.339-6.013,19.134-16.93,19.134s-16.93-6.795-16.93-19.134V0H40.5V33.995c0,15.726,7.071,24.037,20.447,24.037s20.447-8.311,20.447-24.037V0Z" transform="translate(18.264 -0.001)" fill="#fff"/><path d="M83.163,53.863V0H79.646V57.216h32V53.863Z" transform="translate(35.919 -0.001)" fill="#fff"/><path d="M111.231,53.863V29.143h27.421V25.789H111.231V3.354h28.155V0H107.712V57.216h32.326V53.863Z" transform="translate(48.576 -0.001)" fill="#fff"/><path d="M152,0H138.649V57.216H152c19.343,0,30.893-10.694,30.893-28.607S171.34,0,152,0m27.212,28.609c0,16.521-9.693,25.254-28.029,25.254h-9.013V3.355h9.013c18.336,0,28.029,8.732,28.029,25.254" transform="translate(62.528 -0.001)" fill="#fff"/></svg>`;

type Grp = { key: string; hero: string | null; subs: { key: string; items: Product[] }[] };

function buildMagazineHtml(groups: Grp[], title: string, total: number, coverHero: string | null): string {
  const today = new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
  const card = (p: Product) => {
    const img = heroImage(p.images);
    const sum = summaryFor(p);
    const specs = keySpecs(p.specs);
    return `<article class="card">
      <div class="ph">${img ? `<img src="${thumbUrl(img, 520)}" alt="">` : '<span class="ph-x">◻</span>'}</div>
      <div class="bd">
        <div class="nm">${esc(p.name)}</div>
        <div class="sku">${esc(p.sku)}</div>
        ${sum ? `<div class="sm">${esc(sum)}</div>` : ''}
        ${specs.length ? `<div class="sp">${specs.map(s => `<div><span>${esc(s.label)}</span><b>${esc(s.value)}</b></div>`).join('')}</div>` : ''}
      </div>
    </article>`;
  };
  const dividers = groups.map(g => {
    const n = g.subs.reduce((a, s) => a + s.items.length, 0);
    const bg = g.hero
      ? `background-image:linear-gradient(0deg, rgba(20,32,36,.92) 0%, rgba(20,32,36,.35) 55%, rgba(20,32,36,.15) 100%), url('${thumbUrl(g.hero, 1500)}');background-size:cover;background-position:center;`
      : `background:${BRAND};`;
    const products = g.subs.map(sub => `
      ${sub.key && sub.key !== '—' ? `<h3 class="sub">${esc(sub.key)}</h3>` : ''}
      <div class="grid">${sub.items.map(card).join('')}</div>`).join('');
    return `
      <section class="divider" style="${bg}">
        <div class="d-inner">
          <div class="d-eyebrow">Categoría</div>
          <div class="d-bar"></div>
          <div class="d-name">${esc(g.key)}</div>
          <div class="d-count">${n} productos · NULED</div>
        </div>
      </section>
      <div class="cat-body">${products}</div>`;
  }).join('');

  const coverBg = coverHero
    ? `background-image:linear-gradient(0deg, rgba(20,32,36,.9), rgba(20,32,36,.55)), url('${thumbUrl(coverHero, 1600)}');background-size:cover;background-position:center;`
    : `background:${BRAND};`;

  return `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(title)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600;700&family=Roboto:wght@300;400;500;700&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}
  html,body{margin:0;background:#e6e6e6;font-family:'Roboto',sans-serif;color:#20302f;}
  @page{size:letter;margin:16mm 15mm;}
  @page bleed{margin:0;}
  .cover,.divider{page:bleed;break-before:page;height:279.4mm;width:100%;color:#fff;position:relative;overflow:hidden;}
  .cover:first-child{break-before:avoid;}
  .pagedjs_page{background:#fff;box-shadow:0 3px 16px rgba(0,0,0,.2);margin:0 auto 18px;}
  /* Portada */
  .cover{display:flex;flex-direction:column;justify-content:space-between;padding:26mm 22mm;${coverBg}}
  .cover .c-top{opacity:.92}
  .cover .c-mid h1{font-family:'Montserrat';font-weight:300;font-size:40pt;letter-spacing:-1px;line-height:1.02;margin:0}
  .cover .c-bar{height:5px;width:78px;background:${ACCENT};margin-bottom:14px}
  .cover .c-tag{font-size:10pt;letter-spacing:3px;text-transform:uppercase;color:rgba(255,255,255,.8);margin-top:14px}
  .cover .c-bottom{font-size:9pt;color:rgba(255,255,255,.75);line-height:1.7}
  /* Divisor de categoría */
  .divider{display:flex;flex-direction:column;justify-content:flex-end;padding:30mm 22mm;}
  .d-eyebrow{font-size:9pt;letter-spacing:3px;text-transform:uppercase;color:${ACCENT};font-weight:700;margin-bottom:9px}
  .d-bar{height:4px;width:66px;background:${ACCENT};margin-bottom:12px}
  .d-name{font-family:'Montserrat';font-weight:300;font-size:34pt;letter-spacing:-.5px;line-height:1}
  .d-count{font-size:9pt;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,.78);margin-top:11px}
  /* Contenido */
  .cat-body{padding-top:2mm}
  .sub{font-family:'Montserrat';font-weight:600;font-size:11pt;letter-spacing:1px;text-transform:uppercase;color:${BRAND};border-bottom:1px solid #dfe3e0;padding-bottom:5px;margin:6mm 0 4mm;break-after:avoid;}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:6mm;}
  .card{border:1px solid #e2e6e3;border-radius:3px;overflow:hidden;background:#fff;break-inside:avoid;display:flex;flex-direction:column;}
  .card .ph{height:44mm;background:#fff;border-bottom:1px solid #eef1ef;display:flex;align-items:center;justify-content:center;position:relative;}
  .card .ph img{max-width:100%;max-height:100%;object-fit:contain;padding:6mm;}
  .card .ph-x{color:#c8cfca;font-size:26pt}
  .card .bd{padding:4mm 4.5mm 5mm;}
  .card .nm{font-family:'Montserrat';font-weight:700;font-size:10.5pt;color:${BRAND};line-height:1.2}
  .card .sku{font-family:'Roboto Mono',monospace;font-size:7.5pt;color:#8a938e;margin-top:2px;letter-spacing:.3px}
  .card .sm{font-size:8pt;color:#4b565222;color:#4b5652;margin-top:5px;line-height:1.45}
  .card .sp{margin-top:6px;display:grid;grid-template-columns:1fr 1fr;gap:2px 8px}
  .card .sp div{font-size:7.5pt;line-height:1.3}
  .card .sp span{color:#8a938e;text-transform:uppercase;letter-spacing:.2px;font-size:6.6pt;display:block}
  .card .sp b{color:${BRAND}}
</style></head><body>
  <section class="cover">
    <div class="c-top">${LOGO}</div>
    <div class="c-mid"><div class="c-bar"></div><h1>${esc(title)}</h1><div class="c-tag">Iluminación LED · Mayorista Lutron</div></div>
    <div class="c-bottom">${total} productos · ${today}<br>nuled.mx · ventas@nuled.mx<br><span style="opacity:.7">Precios y disponibilidad bajo cotización.</span></div>
  </section>
  ${dividers}
  <script src="https://unpkg.com/pagedjs/dist/paged.polyfill.js"></script>
</body></html>`;
}

export function CatalogoPdf() {
  const [params] = useSearchParams();
  const idsParam = params.get('ids') ?? '';
  const brand = params.get('brand');
  const titulo = params.get('titulo') ?? 'Catálogo NULED';
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const cols = 'id, sku, brand_code, name, short_description, description, specs, images, category_paths';
      const ids = idsParam.split(',').map(s => s.trim()).filter(Boolean);
      let prods: Product[] = [];
      if (ids.length > 0) {
        const { data } = await supabase.from('products').select(cols).in('id', ids);
        const byId: Record<string, Product> = {};
        for (const p of (data as Product[]) ?? []) byId[p.id] = p;
        prods = ids.map(id => byId[id]).filter(Boolean);
      } else {
        let q = supabase.from('products_public').select(cols).eq('status', 'active');
        if (brand === 'nuled') q = q.or('brand_code.eq.nuled,brand_code.is.null'); else if (brand === 'lutron') q = q.eq('brand_code', 'lutron');
        const { data } = await q.limit(2000);
        prods = ((data as Product[]) ?? []).filter(p => heroImage(p.images));
        prods.sort((a, b) => (a.category_paths?.[0] ?? '').localeCompare(b.category_paths?.[0] ?? '') || (a.category_paths?.[1] ?? '').localeCompare(b.category_paths?.[1] ?? '') || a.name.localeCompare(b.name));
      }
      // Agrupar L1 -> L2
      const groups: Grp[] = []; const gi: Record<string, number> = {};
      let coverHero: string | null = null;
      for (const p of prods) {
        if (!coverHero) { const a = appImage(p.images); if (a) coverHero = a; }
        const l1 = catL1(p);
        if (gi[l1] === undefined) { gi[l1] = groups.length; groups.push({ key: l1, hero: null, subs: [] }); }
        const g = groups[gi[l1]];
        if (!g.hero) { const a = appImage(p.images); if (a) g.hero = a; }
        const l2 = catL2(p) ?? '—';
        let sub = g.subs.find(s => s.key === l2);
        if (!sub) { sub = { key: l2, items: [] }; g.subs.push(sub); }
        sub.items.push(p);
      }
      setHtml(prods.length ? buildMagazineHtml(groups, titulo, prods.length, coverHero) : '');
      setLoading(false);
    })();
  }, [idsParam, brand, titulo]);

  const print = () => { const w = iframeRef.current?.contentWindow; if (w) { w.focus(); w.print(); } };

  if (loading) return <div style={{ padding: 40, fontFamily: theme.font.sans }}>Cargando catálogo…</div>;
  if (!html) return <div style={{ padding: 40, fontFamily: theme.font.sans }}>Sin productos para el catálogo. Usa ?brand=nuled o selecciona productos.</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#e6e6e6' }}>
      <div style={{ background: BRAND, color: '#fff', padding: '12px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: theme.font.sans }}>
        <span style={{ fontSize: 12, opacity: 0.8 }}>Catálogo NULED · vista de revista</span>
        <button onClick={print} style={{ background: ACCENT, color: BRAND, border: 'none', padding: '9px 20px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer', fontFamily: theme.font.sans }}>📥 Descargar / Imprimir PDF</button>
      </div>
      <iframe ref={iframeRef} srcDoc={html} title="Catálogo" style={{ flex: 1, width: '100%', border: 'none' }} />
    </div>
  );
}
